import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { auth } from "../../config/firebaseConfig";
import { useAuth } from "../../context/AuthContext";
import {
  handleProductChange,
  normalizeCountryCode,
  getCarrierIdByProduct,
} from "../my_parcel/shipmentUtils";

const Data = () => {
  const { isAuthenticated } = useAuth();
  const [employeeID, setEmployeeID] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  const [address3, setAddress3] = useState("");
  const [postcode, setPostcode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [workmail, setWorkmail] = useState("");
  const [privatemail, setPrivateEmail] = useState("");
  const [worknumber, setWorknumber] = useState("");
  const [persoonnummer, setPersoonnummer] = useState("");
  const [isEditingWorkNumber, setIsEditingWorkNumber] = useState(false);
  const [UPN, setUPN] = useState("");
  const [product, setProduct] = useState("");
  const [label_description, setLabel_description] = useState("");
  const [options, setPackage_options] = useState(null);
  const [carrier, setCarrier] = useState(1);
  const worknumberRef = useRef(null);

  const getAfasUrl = process.env.REACT_APP_GET_AFAS_URL;
  const updateWorkNumberUrl = process.env.REACT_APP_UPDATE_WORKNUMBER_URL;
  const createShipmentUrl = process.env.REACT_APP_CREATE_SHIPMENT_URL;

  const employeeIdRef = useRef(null);

  const resetAll = () => {
    setEmployeeID("");
    setName("");
    setAddress("");
    setAddress2("");
    setAddress3("");
    setPostcode("");
    setCity("");
    setCountry("");
    setWorkmail("");
    setPrivateEmail("");
    setWorknumber("");
    setPersoonnummer("");
    setUPN("");
    setLabel_description("");
    setProduct("");
    setPackage_options("");
    setIsEditingWorkNumber(false);

    // focus terug op personeelsnummer
    setTimeout(() => employeeIdRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (isAuthenticated) {
      setEmployeeID("");
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (employeeID === "") {
      setName("");
      setAddress("");
      setAddress2("");
      setAddress3("");
      setPostcode("");
      setCity("");
      setCountry("");
      setWorkmail("");
      setPrivateEmail("");
      setWorknumber("");
      setPersoonnummer("");
      setUPN("");
      setLabel_description("");
      setProduct("");
    }
  }, [employeeID]);

  useEffect(() => {
    if (employeeID) {
      const timeout = setTimeout(() => {
        setEmployeeID("");
      }, 5 * 60 * 1000); // 5 minuten in ms

      return () => clearTimeout(timeout); // opschonen bij nieuwe zoekactie of unmount
    }
  }, [employeeID]);

  useEffect(() => {
    if (product && label_description) {
      // 1. Haal ALLEEN de opties op (GEEN carrier meer hierin)
      const updatedOptions = handleProductChange(product, label_description);
      setPackage_options(updatedOptions);

      // 2. Haal de carrier APART op met de nieuwe helper functie
      setCarrier(getCarrierIdByProduct(product));
    }
  }, [label_description, product]);

  const handleSearch = async (event) => {
    event.preventDefault();
    if (!employeeID.trim()) {
      return;
    }

    try {
      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(`${getAfasUrl}?employeeID=${employeeID}`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      const responseData = await response.json();

      if (Object.keys(responseData).length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Niet gevonden!",
          text: `Geen gegevens gevonden in AFAS voor personeelsnummer ${employeeID}.`,
          showConfirmButton: true,
          width: "400px",
          customClass: {
            popup: "small-swal",
          },
        });
        setEmployeeID("");
        return;
      }

      setEmployeeID(responseData.Medewerker);
      setName(responseData.Naam);
      setAddress(responseData.Straat);
      setAddress2(String(responseData.Huisnummer));
      setAddress3(responseData.Toev_aan_huisnr);
      setPostcode(responseData.Postcode);
      setCity(responseData.Woonplaats);
      setCountry(responseData.Land);
      setWorkmail(responseData["Gebruiker_e-mail"]);
      setWorknumber(responseData.Mobiel_werk);
      setPersoonnummer(responseData.Mobiel_prive);
      setPrivateEmail(responseData["E-mail_prive"]);
      setUPN(responseData.UPN);
    } catch (error) {
      console.error("Fout bij ophalen AFAS:", error);
      Swal.fire({
        icon: "error",
        title: "Fout!",
        text: `Fout bij ophalen gegevens uit AFAS: ${error}`,
        showConfirmButton: true,
        width: "400px",
        customClass: {
          popup: "small-swal",
        },
      });
    }
  };

  const handleEditClick = () => {
    if (!isEditingWorkNumber) {
      setIsEditingWorkNumber(true);
      setTimeout(() => worknumberRef.current.focus(), 0);
    } else {
      handleWorkNumberSave();
    }
  };

  const handleWorkNumberSave = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(updateWorkNumberUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ employeeID, worknumber }),
      });

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Succes!",
          text: "Werknummer succesvol opgeslagen!",
          timer: 3000,
          showConfirmButton: false,
          width: "400px",
          customClass: {
            popup: "small-swal",
          },
        });
        setIsEditingWorkNumber(false);
      } else {
        throw new Error();
      }
    } catch (error) {
      console.error("Fout bij het opslaan van werknummer:", error);
      Swal.fire({
        icon: "error",
        title: "Fout!",
        text: `Er is een fout opgetreden bij het opslaan van het werknummer ${error}.`,
        timer: 3000,
        showConfirmButton: false,
        width: "400px",
        customClass: {
          popup: "small-swal",
        },
      });
    }
  };

  const onProductChange = (e) => {
    const selectedProduct = e.target.value;
    setProduct(selectedProduct);

    // 1. Haal ALLEEN de opties op (GEEN carrier hierin)
    const options = handleProductChange(selectedProduct, label_description);
    setPackage_options(options);

    // 2. Carrier apart opslaan via de nieuwe helper functie
    setCarrier(getCarrierIdByProduct(selectedProduct));
  };

  const handleCreateShipment = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(createShipmentUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          label_description,
          country_label: normalizeCountryCode(country),
          postcode,
          city,
          address,
          address2,
          address3,
          name,
          privatemail,
          worknumber,
          options,
          product,
          carrier,
        }),
      });

      if (!response.ok) throw new Error("Zending mislukt");

      Swal.fire({
        icon: "success",
        title: "Label Aangemaakt!",
        text: `Label met kenmerk "${label_description}" is succesvol aangemaakt.`,
        timer: 3000,
        showConfirmButton: false,
        width: "400px",
        customClass: { popup: "small-swal" },
      });

      // ✅ hier resetten, na succes
      resetAll();
    } catch (error) {
      console.error("Fout bij aanmaken zending:", error);
      Swal.fire({
        icon: "error",
        title: "Fout!",
        text: "Er is een fout opgetreden bij het aanmaken van de zending.",
        timer: 3000,
        showConfirmButton: false,
        width: "400px",
        customClass: { popup: "small-swal" },
      });
    }
  };

  return (
    <div>
      <div className="main">
        <div className="left-column">
          <h3 className="formheader">Personeelsgegevens</h3>
          <hr></hr>
          <br></br>
          <form className="order-form" onSubmit={handleSearch}>
            <label htmlFor="employeeID">Personeelsnummer:</label>
            <input
              type="text"
              id="employeeID"
              ref={employeeIdRef}
              value={employeeID || ""}
              onChange={(e) => setEmployeeID(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Tab") handleSearch(e);
                if (e.key === "Enter") handleSearch(e);
              }}
              required
              autoFocus
              autoComplete="off"
            />
            <button className="formbutton" type="submit" onClick={handleSearch}>
              <span className="material-icons">search</span>
            </button>
            <label htmlFor="name">Naam:</label>
            <input
              type="text"
              id="name"
              value={name || ""}
              onChange={(e) => setName(e.target.value)}
              disabled="disabled"
            />
            <div></div>
            <label htmlFor="address">Straat:</label>
            <input
              type="text"
              id="address"
              value={address || ""}
              onChange={(e) => setAddress(e.target.value)}
              disabled="disabled"
            />
            <div></div>
            <label htmlFor="address">Huisnummer:</label>
            <input
              type="text"
              id="address2"
              value={address2 || ""}
              onChange={(e) => setAddress2(e.target.value)}
              disabled="disabled"
            />
            <div></div>
            <label htmlFor="address">Toevoeging:</label>
            <input
              type="text"
              id="address3"
              value={address3 ?? ""}
              onChange={(e) => setAddress3(e.target.value)}
              disabled="disabled"
            />
            <div></div>
            <label htmlFor="postcode">Postcode:</label>
            <input
              type="text"
              id="postcode"
              value={postcode || ""}
              onChange={(e) => setPostcode(e.target.value)}
              disabled="disabled"
            />
            <div></div>
            <label htmlFor="city">Woonplaats:</label>
            <input
              type="text"
              id="city"
              value={city || ""}
              onChange={(e) => setCity(e.target.value)}
              disabled="disabled"
            />
            <div></div>
            <label htmlFor="country">Land:</label>
            <input
              type="text"
              id="country"
              value={
                {
                  NL: "Nederland",
                  B: "België",
                  D: "Duitsland",
                }[country?.toUpperCase()] || country
              }
              disabled
            />
            <div></div>
            <label htmlFor="city">Werk e-mailadres:</label>
            <input
              type="text"
              id="workmail"
              value={workmail || ""}
              onChange={(e) => setWorkmail(e.target.value)}
              disabled="disabled"
            />
            <div></div>
            <label htmlFor="worknumber">Werk nummer:</label>
            <input
              type="text"
              id="worknumber"
              ref={worknumberRef}
              value={worknumber || ""}
              onChange={(e) => setWorknumber(e.target.value)}
              disabled={!isEditingWorkNumber}
              className={
                !isEditingWorkNumber ? "input-disabled" : "input-active"
              }
            />
            <button
              type="button"
              className="formbutton"
              onClick={handleEditClick}
              disabled={!employeeID.trim()}
            >
              <span className="material-icons">
                {isEditingWorkNumber ? "save" : "edit"}
              </span>
            </button>

            <label htmlFor="UPN">UPN:</label>
            <input
              type="text"
              id="upn"
              value={UPN || ""}
              onChange={(e) => setWorknumber(e.target.value)}
              disabled="disabled"
            />
            <div></div>
            <h3 className="myparcel">MyParcel</h3>
            <div></div>
            <div></div>
            <label htmlFor="kenmerk">Kenmerk:</label>
            <input
              type="text"
              id="kenmerk"
              placeholder="Ticketnummer"
              value={label_description || ""}
              onChange={(e) => setLabel_description(e.target.value)}
              autoComplete="off"
              required
              disabled={!name.trim()}
            />
            <div className="required_field">*</div>
            <label htmlFor="product">Product:</label>
            <select id="product" value={product} onChange={onProductChange}>
              <option value="">Selecteer Product</option>
              <option value="Startpakket">Startpakket</option>
              <option value="Chromebook">Chromebook</option>
              <option value="Telefoon">Telefoon</option>
              <option value="Simkaart">Simkaart</option>
              <option value="OTP Token">OTP Token</option>
              <option value="Oplader Telefoon">Oplader Telefoon</option>
              <option value="Oplader Chromebook">Oplader Chromebook</option>
              <option value="Bumper 1 Stuks">Bumper 1 Stuks</option>
              <option value="Bumpers 2 Stuks">Bumpers 2 Stuks</option>
              <option value="Bumpers 4 Stuks">Bumpers 4 Stuks</option>
              <option value="Bumpers">Bumpers Maximaal 12 Stuks</option>
              <option value="Chromebook sleeve">Chromebook Sleeve</option>
            </select>
            <div className="required_field">*</div>
            <div></div>
            <button
              className="order"
              type="submit"
              onClick={handleCreateShipment}
              disabled={!label_description.trim() || !product}
            >
              MyParcel zending aanmaken
            </button>
          </form>
          <br></br>
          <hr></hr>
          <br></br>
          <div className="required_text">
            Bij het aanmaken van een label zijn velden met een * verplicht.
          </div>
        </div>
        <div className="right-column">
          <h3 className="formheader">Privé Gegevens</h3>
          <hr></hr>
          <br></br>
          <div className="pr-data">
            <label>Prive mobiel:</label>
            <input type="text" value={persoonnummer} disabled="disabled" />
            <div></div>
            <div></div>
            <label htmlFor="topdesk-chromebook">Prive email:</label>
            <input type="text" value={privatemail} disabled="disabled" />
            <div></div>
            <div></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Data;
